import { useState, useMemo } from 'react';
import {
  Download,
  ArrowLeftRight,
  Loader2,
  Printer,
  Filter,
  ArrowUp,
  ArrowDown,
  RotateCcw,
  Trash2,
  Package,
  Globe,
  MapPin,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useLocationStore } from '@/store/location';
import { useLocations } from '@/hooks/useLocations';
import { useCurrency } from '@/hooks/useCurrency';
import { useReportPrint } from '@/hooks/useReportPrint';
import { format } from 'date-fns';
import { DateRangeSelect, type DateRangeValue } from '@/components/ui/date-range-select';
import { datePresets, presetValue } from '@/lib/datePresets';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LineChart,
  Line,
} from 'recharts';

type TransactionType = 'all' | 'adjusted' | 'waste' | 'returned' | 'transferred' | 'received' | 'sold';
const DATE_RANGE_PRESETS = datePresets('today', 'week', 'month', 'last_month');

const TRANSACTION_TYPES: { value: TransactionType; label: string; icon: any; color: string }[] = [
  { value: 'all', label: 'All Types', icon: ArrowLeftRight, color: 'hsl(215, 16%, 47%)' },
  { value: 'adjusted', label: 'Adjusted', icon: ArrowLeftRight, color: 'hsl(217, 91%, 60%)' },
  { value: 'waste', label: 'Waste', icon: Trash2, color: 'hsl(0, 84%, 60%)' },
  { value: 'returned', label: 'Returned', icon: RotateCcw, color: 'hsl(142, 76%, 36%)' },
  { value: 'transferred', label: 'Transferred', icon: ArrowLeftRight, color: 'hsl(263, 70%, 50%)' },
  { value: 'received', label: 'Received', icon: ArrowDown, color: 'hsl(142, 76%, 36%)' },
  { value: 'sold', label: 'Sold', icon: ArrowUp, color: 'hsl(25, 95%, 53%)' },
];

export default function AdjustmentReports() {
  const { currentLocation } = useLocationStore();
  const { locations } = useLocations();
  const { formatCurrency } = useCurrency();
  const { printRef, handlePrint } = useReportPrint({ title: 'Adjustment report' });

  const [selectedLocationId, setSelectedLocationId] = useState<string>(currentLocation?.id || 'all');
  const [transactionType, setTransactionType] = useState<TransactionType>('all');
  const [dateSel, setDateSel] = useState<DateRangeValue>(() => presetValue('month'));

  // Determine which location to use
  const queryLocationId = selectedLocationId === 'all' ? currentLocation?.id : selectedLocationId;
  const selectedLocation = locations.find(l => l.id === selectedLocationId);

  // Calculate date filters
  const dateFilters = useMemo(
    () => ({ start: dateSel.start.toISOString(), end: dateSel.end.toISOString() }),
    [dateSel]
  );

  const dateRangeLabel = useMemo(() => {
    const start = new Date(dateFilters.start);
    const end = new Date(dateFilters.end);
    return `${format(start, 'MMM d, yyyy')} to ${format(end, 'MMM d, yyyy')}`;
  }, [dateFilters]);

  // Fetch adjustment transactions
  const { data: transactions, isLoading } = useQuery({
    queryKey: ['adjustment-report', queryLocationId, transactionType, dateFilters],
    queryFn: async () => {
      if (!queryLocationId) return [];

      let query = supabase
        .from('inventory_transactions')
        .select(`
          id,
          transaction_type,
          quantity_change,
          quantity_before,
          quantity_after,
          notes,
          created_at,
          inventory_item:inventory_items(
            id,
            product:products(id, name, sku, cost)
          ),
          user:profiles(first_name, last_name)
        `)
        .gte('created_at', dateFilters.start)
        .lte('created_at', dateFilters.end)
        .order('created_at', { ascending: false });

      // Filter by location through inventory_items
      const { data: locationItems } = await supabase
        .from('inventory_items')
        .select('id')
        .eq('location_id', queryLocationId);

      if (locationItems) {
        query = query.in('inventory_item_id', locationItems.map(i => i.id));
      }

      if (transactionType !== 'all') {
        query = query.eq('transaction_type', transactionType);
      }

      const { data, error } = await query.limit(500);

      if (error) throw error;
      return data || [];
    },
    enabled: !!queryLocationId,
  });

  // Process transactions for display
  const processedTransactions = useMemo(() => {
    if (!transactions) return [];

    return transactions.map(txn => {
      const item = txn.inventory_item as any;
      const product = item?.product;
      const user = txn.user as any;
      const costImpact = (product?.cost || 0) * Math.abs(txn.quantity_change);

      return {
        id: txn.id,
        date: txn.created_at,
        type: txn.transaction_type,
        productName: product?.name || 'Unknown Product',
        sku: product?.sku || '-',
        quantityChange: txn.quantity_change,
        quantityBefore: txn.quantity_before,
        quantityAfter: txn.quantity_after,
        costImpact,
        notes: txn.notes || '',
        userName: user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : 'System',
      };
    });
  }, [transactions]);

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    if (!processedTransactions.length) return null;

    const byType: Record<string, { count: number; totalChange: number; costImpact: number }> = {};
    
    processedTransactions.forEach(txn => {
      if (!byType[txn.type]) {
        byType[txn.type] = { count: 0, totalChange: 0, costImpact: 0 };
      }
      byType[txn.type].count += 1;
      byType[txn.type].totalChange += txn.quantityChange;
      byType[txn.type].costImpact += txn.costImpact;
    });

    const totalTransactions = processedTransactions.length;
    const netChange = processedTransactions.reduce((sum, t) => sum + t.quantityChange, 0);
    const totalCostImpact = Object.values(byType).reduce((sum, t) => sum + t.costImpact, 0);

    return {
      totalTransactions,
      netChange,
      totalCostImpact,
      byType,
    };
  }, [processedTransactions]);

  // Chart data - transactions by type
  const typeChartData = useMemo(() => {
    if (!summaryStats?.byType) return [];

    return Object.entries(summaryStats.byType)
      .map(([type, data]) => {
        const config = TRANSACTION_TYPES.find(t => t.value === type);
        return {
          name: config?.label || type,
          count: data.count,
          costImpact: data.costImpact,
          fill: config?.color || 'hsl(215, 16%, 47%)',
        };
      })
      .sort((a, b) => b.count - a.count);
  }, [summaryStats]);

  // Chart data - daily trend
  const dailyTrendData = useMemo(() => {
    if (!processedTransactions.length) return [];

    const dailyMap = new Map<string, number>();
    processedTransactions.forEach(txn => {
      const date = format(new Date(txn.date), 'MMM d');
      dailyMap.set(date, (dailyMap.get(date) || 0) + 1);
    });

    return Array.from(dailyMap.entries())
      .map(([date, count]) => ({ date, count }))
      .reverse();
  }, [processedTransactions]);

  const chartConfig: ChartConfig = {
    count: { label: 'Transactions', color: 'hsl(217, 91%, 60%)' },
    costImpact: { label: 'Cost Impact', color: 'hsl(25, 95%, 53%)' },
  };

  // Export CSV
  const exportToCSV = () => {
    if (!processedTransactions.length) return;

    const headers = ['Date', 'Type', 'Product', 'SKU', 'Change', 'Before', 'After', 'Cost Impact', 'Notes', 'User'];
    const rows = processedTransactions.map(txn => [
      format(new Date(txn.date), 'yyyy-MM-dd HH:mm'),
      txn.type,
      txn.productName,
      txn.sku,
      txn.quantityChange,
      txn.quantityBefore,
      txn.quantityAfter,
      txn.costImpact.toFixed(2),
      txn.notes,
      txn.userName,
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `adjustment-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getTypeBadge = (type: string) => {
    const config = TRANSACTION_TYPES.find(t => t.value === type);
    const Icon = config?.icon || ArrowLeftRight;
    
    const colors: Record<string, string> = {
      adjusted: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      waste: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      returned: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      transferred: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      received: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      sold: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
    };

    return (
      <Badge variant="secondary" className={colors[type] || ''}>
        <Icon className="h-3 w-3 mr-1" />
        {config?.label || type}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-semibold">Adjustment Reports</h1>
          <p className="text-sm text-muted-foreground">
            View inventory adjustments, stock corrections, and movement history
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-2" onClick={handlePrint}>
            <Printer className="h-4 w-4" />
            Print
          </Button>
          <Button variant="outline" size="sm" className="gap-2" onClick={exportToCSV}>
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        {/* Location Selector */}
        <Select value={selectedLocationId} onValueChange={setSelectedLocationId}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Select location" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              <div className="flex items-center gap-2">
                <Globe className="h-3.5 w-3.5" />
                <span>All locations</span>
              </div>
            </SelectItem>
            {locations.length > 0 && <div className="my-1 h-px bg-border" />}
            {locations.map((loc) => (
              <SelectItem key={loc.id} value={loc.id}>
                <div className="flex items-center gap-2">
                  <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                  <span>{loc.name}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={transactionType} onValueChange={(v) => setTransactionType(v as TransactionType)}>
          <SelectTrigger className="w-[160px]">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TRANSACTION_TYPES.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <DateRangeSelect presets={DATE_RANGE_PRESETS} value={dateSel} onChange={setDateSel} />
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Transactions</span>
              <ArrowLeftRight className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {isLoading ? '...' : summaryStats?.totalTransactions || 0}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Net Quantity Change</span>
              <Package className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className={`text-2xl font-bold mt-2 ${
              (summaryStats?.netChange || 0) > 0 ? 'text-green-600' : 
              (summaryStats?.netChange || 0) < 0 ? 'text-red-600' : ''
            }`}>
              {isLoading ? '...' : (
                (summaryStats?.netChange || 0) > 0 ? '+' : ''
              ) + (summaryStats?.netChange || 0)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Waste (Count)</span>
              <Trash2 className="h-4 w-4 text-red-500" />
            </div>
            <p className="text-2xl font-bold mt-2 text-red-600">
              {isLoading ? '...' : summaryStats?.byType?.waste?.count || 0}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Cost Impact</span>
              <ArrowLeftRight className="h-4 w-4 text-amber-500" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {isLoading ? '...' : formatCurrency(summaryStats?.totalCostImpact || 0)}
            </p>
          </CardContent>
        </Card>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Transactions by Type */}
            <Card>
              <CardHeader>
                <CardTitle>Transactions by Type</CardTitle>
                <CardDescription>Breakdown of adjustment types</CardDescription>
              </CardHeader>
              <CardContent>
                {typeChartData.length > 0 ? (
                  <ChartContainer config={chartConfig} className="h-[250px]">
                    <BarChart data={typeChartData}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis
                        dataKey="name"
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                      />
                      <ChartTooltip
                        content={
                          <ChartTooltipContent
                            valueFormatter={(value) => `${value} transactions`}
                          />
                        }
                      />
                      <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                        {typeChartData.map((entry, index) => (
                          <Bar key={index} dataKey="count" fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ChartContainer>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                    No transaction data
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Daily Trend */}
            <Card>
              <CardHeader>
                <CardTitle>Daily Trend</CardTitle>
                <CardDescription>Transaction volume over time</CardDescription>
              </CardHeader>
              <CardContent>
                {dailyTrendData.length > 0 ? (
                  <ChartContainer config={chartConfig} className="h-[250px]">
                    <LineChart data={dailyTrendData}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis
                        dataKey="date"
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                      />
                      <ChartTooltip
                        content={
                          <ChartTooltipContent
                            valueFormatter={(value) => `${value} transactions`}
                          />
                        }
                      />
                      <Line
                        type="monotone"
                        dataKey="count"
                        stroke="hsl(217, 91%, 60%)"
                        strokeWidth={2}
                        dot={{ fill: 'hsl(217, 91%, 60%)', strokeWidth: 0, r: 4 }}
                      />
                    </LineChart>
                  </ChartContainer>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                    No trend data
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Transaction Table */}
          <Card>
            <CardHeader>
              <CardTitle>Transaction History</CardTitle>
              <CardDescription>
                {processedTransactions.length} transactions in selected period
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50 hover:bg-muted/50">
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead className="text-right">Change</TableHead>
                    <TableHead className="text-right">Before</TableHead>
                    <TableHead className="text-right">After</TableHead>
                    <TableHead className="text-right">Cost Impact</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {processedTransactions.slice(0, 100).map((txn) => (
                    <TableRow key={txn.id}>
                      <TableCell className="whitespace-nowrap">
                        {format(new Date(txn.date), 'MMM d, yyyy h:mm a')}
                      </TableCell>
                      <TableCell>{getTypeBadge(txn.type)}</TableCell>
                      <TableCell>
                        <div>
                          <span className="font-medium">{txn.productName}</span>
                          {txn.sku !== '-' && (
                            <p className="text-xs text-muted-foreground">{txn.sku}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className={`text-right tabular-nums font-medium ${
                        txn.quantityChange > 0 ? 'text-green-600' : 
                        txn.quantityChange < 0 ? 'text-red-600' : ''
                      }`}>
                        {txn.quantityChange > 0 ? '+' : ''}{txn.quantityChange}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {txn.quantityBefore ?? '-'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {txn.quantityAfter ?? '-'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(txn.costImpact)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{txn.userName}</TableCell>
                      <TableCell className="max-w-[200px] truncate text-muted-foreground">
                        {txn.notes || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {processedTransactions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                        No transactions found for the selected filters
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              {processedTransactions.length > 100 && (
                <p className="text-sm text-muted-foreground mt-4 text-center">
                  Showing first 100 of {processedTransactions.length} transactions
                </p>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Print template */}
      <div ref={printRef} className="print-source">
        <div className="print-header">
          <div>
            <h1>Adjustment Report</h1>
            <div className="print-meta">
              {selectedLocation?.name || currentLocation?.name || 'All Locations'} • {dateRangeLabel}
            </div>
            <div className="print-meta">
              Filter: {TRANSACTION_TYPES.find(t => t.value === transactionType)?.label || 'All'}
            </div>
          </div>
          <div className="print-meta">
            Generated on {new Date().toLocaleString()}
          </div>
        </div>

        <div className="print-summary">
          <div className="print-card">
            <div className="label">Total Transactions</div>
            <div className="value">{summaryStats?.totalTransactions || 0}</div>
          </div>
          <div className="print-card">
            <div className="label">Net Change</div>
            <div className="value">{summaryStats?.netChange || 0}</div>
          </div>
          <div className="print-card">
            <div className="label">Total Cost Impact</div>
            <div className="value">{formatCurrency(summaryStats?.totalCostImpact || 0)}</div>
          </div>
        </div>

        <div className="print-section">
          <h2>Transaction History</h2>
          {processedTransactions.length > 0 ? (
            <table className="print-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Product</th>
                  <th>Change</th>
                  <th>Before</th>
                  <th>After</th>
                  <th>Cost Impact</th>
                  <th>User</th>
                </tr>
              </thead>
              <tbody>
                {processedTransactions.map((txn) => (
                  <tr key={txn.id}>
                    <td>{format(new Date(txn.date), 'MMM d, yyyy h:mm a')}</td>
                    <td>{txn.type}</td>
                    <td>{txn.productName}</td>
                    <td>{txn.quantityChange > 0 ? '+' : ''}{txn.quantityChange}</td>
                    <td>{txn.quantityBefore ?? '-'}</td>
                    <td>{txn.quantityAfter ?? '-'}</td>
                    <td>{formatCurrency(txn.costImpact)}</td>
                    <td>{txn.userName}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="print-meta">No transactions found.</div>
          )}
        </div>
      </div>
    </div>
  );
}
